import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMedplumProfile } from '@medplum/react'
import { useSubscription } from '@medplum/react-hooks'
import { useClient, useOrpc } from '@/hooks/useClient'
import type { OrpcClient } from '@/orpc/client'

export type TaskNotification = Awaited<
  ReturnType<OrpcClient['notificationList']>
>['notifications'][number]

/**
 * A mapped prescriber's standing DoseSpot notification counts, or `undefined` for everyone
 * else — non-prescribers have no mapping, so there is nothing to render and no role check
 * needed to decide that.
 *
 * A **state**, not a feed: it carries no id, no read flag and nothing to dismiss, and it
 * clears when DoseSpot's own count clears. That is why it rides beside `notifications`
 * rather than inside it.
 */
export type DoseSpotNotificationCounts = NonNullable<
  Awaited<ReturnType<OrpcClient['notificationList']>>['doseSpot']
>

// In-app task-assignment notifications for the signed-in user. Owned by the
// always-mounted NotificationBell in the shell, so one query + one subscription
// serve the whole app. Kept live by the same `Communication?recipient` WebSocket
// subscription the Messages inbox uses, with a fallback poll so notifications
// still surface within a minute even if the real-time ping doesn't arrive.
export function useTaskNotifications() {
  const orpc = useOrpc()
  const client = useClient()
  const queryClient = useQueryClient()
  const profile = useMedplumProfile()
  const practitionerId = profile?.resourceType === 'Practitioner' ? profile.id : undefined

  // Scope the cache key to the signed-in practitioner. Logout is an SPA
  // navigation that keeps the QueryClient alive, so a user-agnostic key would let
  // a same-browser account switch render the previous user's cached notification
  // titles/boards. A per-user key means a different account never reads it.
  const queryKey = useMemo(() => ['task-notifications', practitionerId ?? 'anon'], [practitionerId])

  const query = useQuery({
    queryKey,
    queryFn: () => client.notificationList({}),
    enabled: !!practitionerId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  )

  // A matching event just triggers a refetch — the list is already server-scoped
  // to this user's task-notifications, so no client-side filtering is needed.
  useSubscription(
    practitionerId ? `Communication?recipient=Practitioner/${practitionerId}` : undefined,
    () => void invalidate(),
  )

  const markRead = useMutation(
    orpc.notificationMarkRead.mutationOptions({ onSuccess: () => void invalidate() }),
  )
  const markAllRead = useMutation(
    orpc.notificationMarkAllRead.mutationOptions({ onSuccess: () => void invalidate() }),
  )
  const deleteOne = useMutation(
    orpc.notificationDelete.mutationOptions({ onSuccess: () => void invalidate() }),
  )

  return {
    notifications: query.data?.notifications ?? [],
    unreadCount: query.data?.unreadCount ?? 0,
    // The bell's second source. It arrives on the same payload as the task feed — and so on
    // the same 60 s poll and the same `Communication?recipient` subscription, which is what
    // makes the DoseSpot badge live with no new plumbing. Absent for everyone who is not a
    // mapped prescriber, and for a poll where the source could not be read.
    doseSpotCounts: query.data?.doseSpot,
    isLoading: query.isLoading,
    markOneRead: (id: string) => markRead.mutate({ id }),
    markAllRead: () => markAllRead.mutate({}),
    markAllReadPending: markAllRead.isPending,
    deleteOne: (id: string) => deleteOne.mutate({ id }),
    refetch: query.refetch,
  }
}
